import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { getConsent, grantConsent, denyConsent, applyStoredConsent } from "@/lib/analytics";

export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    applyStoredConsent();
    if (!getConsent()) setVisible(true);
  }, []);

  // Reserve space at the bottom of the page so the fixed banner never covers content.
  useLayoutEffect(() => {
    if (!visible) {
      document.body.style.paddingBottom = "";
      return;
    }
    const setPad = () => {
      const h = ref.current?.offsetHeight || 0;
      document.body.style.paddingBottom = `${h}px`;
    };
    setPad();
    window.addEventListener("resize", setPad);
    return () => {
      window.removeEventListener("resize", setPad);
      document.body.style.paddingBottom = "";
    };
  }, [visible]);

  if (!visible) return null;

  const accept = () => { grantConsent(); setVisible(false); };
  const decline = () => { denyConsent(); setVisible(false); };

  return (
    <div
      ref={ref}
      data-testid="consent-banner"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-brand-line bg-white/95 px-4 py-3 shadow-lift backdrop-blur"
    >
      <div className="mx-auto flex max-w-4xl flex-col items-center gap-3 sm:flex-row">
        <Cookie size={18} className="shrink-0 text-brand" strokeWidth={1.8} />
        <p className="flex-1 text-center text-xs leading-snug text-slate-600 sm:text-left">
          We use privacy-first analytics to improve your experience. We never record your screen or capture
          your business content. See our{" "}
          <Link to="/privacy" className="font-medium text-brand underline">Privacy Policy</Link>.
        </p>
        <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
          <button
            data-testid="consent-decline-btn"
            onClick={decline}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg border border-brand-line px-4 py-2 text-sm font-medium text-slate-700 transition-all duration-200 hover:bg-slate-50 sm:flex-none"
          >
            Decline
          </button>
          <button
            data-testid="consent-accept-btn"
            onClick={accept}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-brand px-5 py-2 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover sm:flex-none"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
